"use client";

/**
 * R3-33 S4-E Phase 3 — AI 비서 floating widget.
 *
 * 우측 하단 fab 버튼 → 클릭 시 채팅 패널 토글.
 * /chat 페이지로 이동하지 않고 바로 대화 가능.
 * 매장 페이지·트렌드 상세 등 어디서나 사용.
 */
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useChatStore } from "@/store/chatStore";
import { useActiveStore } from "@/store/activeStore";
import { parseMessage } from "@/lib/chatMarkers";
import { ChatCards } from "@/components/chat/ChatCards";
import { FeedbackButtons } from "@/components/chat/FeedbackButtons";

interface ChatMessage {
  id: number | string;
  role: "user" | "assistant";
  content: string;
  cost_krw?: number;
  tools?: string[];
  feedback?: "up" | "down" | null;
}

interface QuotaInfo {
  tier: string;
  daily_quota: number;
  used_today: number;
  remaining: number;
  resets_at: string;
}

interface SendOut {
  session_id: number;
  user_message_id: number;
  assistant_message_id: number;
  assistant_text: string;
  tool_calls_used: { tool_name: string; args: Record<string, unknown> }[];
  elapsed_sec: number;
  cost_krw: number;
  quota_remaining: number;
}

const STORAGE_KEY = "trendrain_chat_session";

// 빠른 질문 칩 (사용자 결정 2026-05-09, 3순위) — 고정 2 + 동적 2
// 동적 칩은 사용자 매장 등록 여부에 따라 노출.
interface QuickChip {
  id: string;
  emoji: string;
  text: string;          // 사용자 화면 라벨 (짧음)
  message: string;       // AI 에 전송할 실제 메시지 (구체)
  requireStore?: boolean;   // 매장 등록자만 노출
}

// 2026-05-12 — 등급 안내 6 핵심 기능과 일관 (트렌드/매장/공급처/AI/레시피 + 사용법)
const QUICK_CHIPS: QuickChip[] = [
  {
    id: "trend-now",
    emoji: "📈",
    text: "요즘 뜨는 디저트 알려줘",
    message: "요즘 뜨는 디저트 트렌드 키워드 알려줘.",
  },
  {
    id: "my-store-stats",
    emoji: "🏪",
    text: "이번주 매출 어때?",
    message: "내 매장 이번주 매출 어때?",
    requireStore: true,
  },
  {
    id: "supplier",
    emoji: "🏭",
    text: "버터떡 어디서 사?",
    message: "버터떡 공급처 추천해줘.",
  },
  {
    id: "recipe",
    emoji: "🍳",
    text: "두바이초콜릿 만드는 법",
    message: "두바이초콜릿 레시피 추천해줘.",
  },
  {
    id: "tax-eligibility",
    emoji: "💰",
    text: "내가 받을 수 있는 절세 알려줘",
    message: "내 매장 정보 기준으로 적용 가능한 세금 감면·공제 후보 알려줘.",
    requireStore: true,
  },
  {
    id: "how-to-use",
    emoji: "❓",
    text: "AI 이미지 어떻게 만들어?",
    message: "AI 이미지 기능 사용법 알려줘.",
  },
];

export function AssistantWidget() {
  const { token, isLoggedIn } = useAuthStore();
  // 사용자 결정 2026-05-08 — floating button 제거, open state 는 외부 store 로 이동.
  // AiToolsSection 의 AI 비서 카드 클릭에서 store.setOpen(true) 트리거.
  const open = useChatStore((s) => s.open);
  const setOpen = useChatStore((s) => s.setOpen);
  const consumePrefill = useChatStore((s) => s.consumePrefill);
  const activeStoreId = useActiveStore((s) => s.activeStoreId);   // 동적 칩 가드용
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ⚠ Hook 들 모두 호출 끝난 후에 conditional return 해야 React Hooks rule 준수.
  // 커뮤니티 영역에서도 표시 — 글쓰기 fab 은 좌측, AI 챗봇은 우측으로 분리됨

  // 세션 id localStorage 복원
  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const sid = Number(saved);
      if (!Number.isNaN(sid)) setSessionId(sid);
    }
  }, []);

  // 패널 열 때 quota + 세션 메시지 로드
  useEffect(() => {
    if (!open || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get<QuotaInfo>("/api/chat/quota", { headers })
      .then((r) => setQuota(r.data))
      .catch(() => setQuota(null));
    if (sessionId) {
      axios
        .get<{ messages: { id: number; role: string; content: string | null; tool_name: string | null; feedback?: "up" | "down" | null }[] }>(
          `/api/chat/sessions/${sessionId}`,
          { headers },
        )
        .then((r) => {
          const msgs: ChatMessage[] = r.data.messages
            .filter((m) => m.role === "user" || (m.role === "assistant" && m.content))
            .map((m) => ({
              id: m.id,
              role: m.role as "user" | "assistant",
              content: m.content || "",
              feedback: m.feedback ?? null,
            }));
          setMessages(msgs);
        })
        .catch(() => {
          // 세션이 사라졌을 수 있음 — 초기화
          setSessionId(null);
          localStorage.removeItem(STORAGE_KEY);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  // 메시지 변경 시 스크롤 맨 아래
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, busy]);

  // 빠른 질문 칩 prefill 자동 전송 (3순위, 2026-05-09)
  // openWithMessage 호출 시 모달 열림 + 메시지 prefill → mount 후 1회 자동 send
  useEffect(() => {
    if (!open || !token || busy) return;
    const prefilled = consumePrefill();
    if (prefilled) {
      // setTimeout 으로 한 tick 미루어 quota/session load useEffect 가 먼저 돌게
      setTimeout(() => send(prefilled), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  const send = async (content: string) => {
    if (!token || !content.trim() || busy) return;
    setBusy(true);
    setError(null);

    // 사용자 메시지 즉시 표시
    const tempUserId = `u-${Date.now()}`;
    setMessages((cur) => [...cur, { id: tempUserId, role: "user", content }]);
    setDraft("");

    try {
      const r = await axios.post<SendOut>(
        "/api/chat/messages",
        {
          session_id: sessionId,
          content,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60_000 },
      );
      // 세션 id 저장
      if (!sessionId) {
        setSessionId(r.data.session_id);
        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEY, String(r.data.session_id));
        }
      }
      // assistant 메시지 추가
      setMessages((cur) => [
        ...cur,
        {
          id: r.data.assistant_message_id,
          role: "assistant",
          content: r.data.assistant_text,
          cost_krw: r.data.cost_krw,
          tools: r.data.tool_calls_used.map((t) => t.tool_name),
        },
      ]);
      // quota 업데이트
      setQuota((q) => q ? { ...q, used_today: q.used_today + 1, remaining: r.data.quota_remaining } : q);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? "전송 실패");
      // 사용자 메시지 롤백
      setMessages((cur) => cur.filter((m) => m.id !== tempUserId));
    } finally {
      setBusy(false);
    }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
    if (typeof window !== "undefined") localStorage.removeItem(STORAGE_KEY);
  };

  if (!isLoggedIn) return null;

  return (
    <>
      {/* fab 버튼 제거 (사용자 결정 2026-05-08) — AiToolsSection 의 AI 비서 카드에서 진입.
          모달 자체는 그대로 유지. open state 는 useChatStore 에서 외부 제어. */}

      {/* 채팅 패널 */}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-20 sm:right-4 z-50 sm:w-[380px] sm:h-[560px] bg-bg sm:rounded-2xl sm:shadow-2xl flex flex-col sm:border sm:border-border">
          {/* 헤더 */}
          <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-primary/10 sm:rounded-t-2xl shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🤖</span>
              <div>
                <p className="text-sm font-bold text-text">AI 비서</p>
                {quota && (
                  <p className="text-2xs text-text-sub">
                    오늘 {quota.used_today}회 사용
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={newSession} className="text-2xs text-text-sub hover:text-text" title="새 대화">
                + 새 대화
              </button>
              <Link href="/chat" onClick={() => setOpen(false)} className="text-2xs text-text-sub hover:text-text" title="전체 보기">
                ↗
              </Link>
              <button onClick={() => setOpen(false)} className="text-text-sub hover:text-text text-lg" aria-label="닫기">
                ✕
              </button>
            </div>
          </div>

          {/* 메시지 영역 */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <p className="text-sm text-text">👋 안녕하세요!</p>
                <p className="text-2xs text-text-sub mt-1 mb-4">
                  트렌드·매장·사용법 무엇이든 물어보세요
                </p>
                {/* 빠른 질문 칩 (3순위) — 사용자 매장 등록 여부 따라 동적 노출 */}
                <div className="space-y-1.5 text-left">
                  {QUICK_CHIPS
                    .filter((c) => !c.requireStore || activeStoreId)
                    .map((c) => (
                      <button
                        key={c.id}
                        onClick={() => send(c.message)}
                        disabled={busy}
                        className="w-full text-2xs px-3 py-2 bg-bg-elevated rounded-lg hover:bg-primary/10 text-text-sub hover:text-text transition-colors text-left disabled:opacity-50"
                      >
                        {c.emoji} {c.text}
                      </button>
                    ))}
                </div>
              </div>
            )}
            {messages.map((m, idx) => {
              // 2026-05-12 — assistant 답변에서 <CARDS> + <FOLLOWUP> 마커 파싱
              const parsed = m.role === "assistant"
                ? parseMessage(m.content)
                : { cleaned: m.content, cards: null, followups: [] as string[] };
              const isLastAssistant = m.role === "assistant" && idx === messages.length - 1;
              return (
                <div key={m.id}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-2xl ${
                      m.role === "user"
                        ? "bg-primary text-bg rounded-br-sm"
                        : "bg-bg-elevated text-text rounded-bl-sm"
                    }`}>
                      <p className="text-xs whitespace-pre-wrap leading-relaxed break-words">{parsed.cleaned}</p>
                      {m.role === "assistant" && m.tools && m.tools.length > 0 && (
                        <p className="text-[10px] text-text-sub mt-1.5 italic">
                          🔧 {m.tools.join(" · ")}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* 카드 임베드 */}
                  {parsed.cards && <ChatCards block={parsed.cards} />}
                  {/* 답변 피드백 (assistant 답변만, 마이그 057) */}
                  {m.role === "assistant" && typeof m.id === "number" && (
                    <FeedbackButtons messageId={m.id} initialRating={m.feedback ?? null} />
                  )}
                  {/* Follow-up 칩 (마지막 assistant 답변에만 표시) */}
                  {isLastAssistant && parsed.followups.length > 0 && !busy && (
                    <div className="mt-2 ml-1 flex flex-col gap-1.5">
                      {parsed.followups.map((q) => (
                        <button
                          key={q}
                          type="button"
                          onClick={() => send(q)}
                          className="text-2xs text-left px-2.5 py-1.5 rounded-full bg-bg-elevated border border-border hover:border-primary/60 hover:text-primary transition-colors w-fit max-w-[85%]"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-bg-elevated text-text-sub px-3 py-2 rounded-2xl rounded-bl-sm text-xs">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-text-sub animate-pulse mr-1" />
                  AI 가 생각 중...
                </div>
              </div>
            )}
          </div>

          {/* 입력 */}
          <div className="border-t border-border p-2 shrink-0">
            {error && <p className="text-2xs text-red-400 mb-1 px-1">{error}</p>}
            <div className="flex gap-1.5">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(draft);
                  }
                }}
                placeholder={
                  quota && quota.remaining <= 0
                    ? "오늘 한도 소진 (내일 0시 리셋)"
                    : "무엇이든 물어보세요"
                }
                rows={1}
                maxLength={2000}
                disabled={busy || (quota?.remaining ?? 1) <= 0}
                className="flex-1 px-2.5 py-2 text-xs bg-bg-elevated border border-border rounded-xl text-text resize-none disabled:opacity-50"
              />
              <button
                onClick={() => send(draft)}
                disabled={busy || !draft.trim() || (quota?.remaining ?? 1) <= 0}
                className="px-3 py-2 bg-primary text-bg rounded-xl text-xs font-bold disabled:opacity-30"
              >
                전송
              </button>
            </div>
            {quota && quota.daily_quota === 0 && (
              <p className="text-2xs text-yellow-400 mt-1 px-1">
                ⓘ {quota.tier} 티어는 미지원 — lite/pro 구독 필요
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
